/**
 * Proposal defence analysis: where the price comes from, where the cost goes,
 * and which lines carry (or lose) margin.
 *
 * Everything here is derived from engine results already fetched for the flow,
 * so it costs no extra calls. Nothing is recalculated: prices, costs and cost
 * lines all come back from the engine, and this module only aggregates and
 * ranks them.
 */
import { ProjectState, ModuleKey, MODULE_LABELS, ALL_MODULE_KEYS } from './spreadSplitData';
import { SpreadPriceState } from './useSpreadPrice';
import { ModulePriceResult, SpreadPriceResult } from './spreadPricingApi';
import { buildCostLines, CostLine } from '../utils/internalCostBreakdown';

/** Margin below this reads as thin enough to be worth a second look. */
export const THIN_MARGIN = 0.15;

// The engine's product id per module, so welding's pre-built cost lines are
// recognised as such by the shared cost-line builder.
const PRODUCT_ID: Record<ModuleKey, string> = {
  welding: 'welding', mt: 'materialTraceability', ndt: 'ndt',
  coating: 'coating', platform: 'platform', retention: 'retention',
};

export interface Contribution {
  label: string;
  amount: number;
  /** Share of the total, 0..1. */
  share: number;
  note?: string;
}

export interface MarginRow {
  label: string;
  price: number;
  cost: number;
  /** Fraction of price, or null when there is no price to take a margin on. */
  margin: number | null;
  flag: 'negative' | 'thin' | null;
}

export interface Defence {
  totalPrice: number;
  totalCost: number;
  totalMargin: number | null;
  priceDrivers: Contribution[];
  costDrivers: Contribution[];
  margins: MarginRow[];
  /** Spreads still loading or failed; their figures are missing from the totals. */
  missingSpreads: number;
}

function doneResults(project: ProjectState, states: Record<string, SpreadPriceState>): SpreadPriceResult[] {
  return project.spreads
    .map((s) => states[s.id])
    .filter((st): st is Extract<SpreadPriceState, { status: 'done' }> => st?.status === 'done')
    .map((st) => st.result);
}

const share = (amount: number, total: number): number => (total > 0 ? amount / total : 0);
const byAmountDesc = (a: Contribution, b: Contribution) => b.amount - a.amount;

/** Sum one module across every priced spread. */
function moduleTotals(results: SpreadPriceResult[], key: ModuleKey): { price: number; cost: number } {
  return results.reduce(
    (acc, r) => {
      const line: ModulePriceResult = r[key];
      return { price: acc.price + line.total, cost: acc.cost + line.cost };
    },
    { price: 0, cost: 0 }
  );
}

/** Every module's cost lines, merged by label across spreads. */
function mergedCostLines(results: SpreadPriceResult[]): CostLine[] {
  const byLabel = new Map<string, CostLine>();
  results.forEach((result) => {
    ALL_MODULE_KEYS.forEach((key) => {
      buildCostLines(PRODUCT_ID[key], result[key].details).forEach((line) => {
        const existing = byLabel.get(line.label);
        // Notes describe one spread's arithmetic, so they are only meaningful
        // while a label appears once. Merged lines drop the note.
        if (existing) {
          byLabel.set(line.label, { ...existing, cost: existing.cost + line.cost, note: '' });
        } else {
          byLabel.set(line.label, { ...line });
        }
      });
    });
  });
  return Array.from(byLabel.values());
}

function marginRow(label: string, price: number, cost: number): MarginRow {
  const margin = price > 0 ? (price - cost) / price : null;
  const flag = margin === null ? null : margin < 0 ? 'negative' : margin < THIN_MARGIN ? 'thin' : null;
  return { label, price, cost, margin, flag };
}

/**
 * Build the defence view for a project.
 *
 * Project-level services are billed once and booked at cost, so they enter the
 * price and the cost at the same figure and show a zero margin.
 */
export function buildDefence(
  project: ProjectState,
  states: Record<string, SpreadPriceState>,
  servicesTotal = 0
): Defence {
  const results = doneResults(project, states);

  const modules = ALL_MODULE_KEYS
    .map((key) => ({ key, ...moduleTotals(results, key) }))
    .filter((m) => m.price > 0 || m.cost > 0);

  const totalPrice = modules.reduce((sum, m) => sum + m.price, 0) + servicesTotal;
  const totalCost = modules.reduce((sum, m) => sum + m.cost, 0) + servicesTotal;

  const priceDrivers: Contribution[] = modules
    .map((m) => ({ label: MODULE_LABELS[m.key], amount: m.price, share: share(m.price, totalPrice) }))
    .concat(servicesTotal > 0
      ? [{ label: 'Welding services', amount: servicesTotal, share: share(servicesTotal, totalPrice) }]
      : [])
    .filter((c) => c.amount > 0)
    .sort(byAmountDesc);

  const costDrivers: Contribution[] = mergedCostLines(results)
    .map((line) => ({ label: line.label, amount: line.cost, share: share(line.cost, totalCost), note: line.note }))
    .concat(servicesTotal > 0
      ? [{ label: 'Welding services', amount: servicesTotal, share: share(servicesTotal, totalCost), note: 'billed at cost' }]
      : [])
    .sort(byAmountDesc);

  const margins = modules
    .map((m) => marginRow(MODULE_LABELS[m.key], m.price, m.cost))
    .sort((a, b) => (a.margin ?? 1) - (b.margin ?? 1));

  return {
    totalPrice,
    totalCost,
    totalMargin: totalPrice > 0 ? (totalPrice - totalCost) / totalPrice : null,
    priceDrivers,
    costDrivers,
    margins,
    missingSpreads: project.spreads.length - results.length,
  };
}
